/**
 * Template variable definitions and resolution utilities.
 * Variables use {{key}} syntax, e.g. {{driver.first_name}}, {{car.make}}, {{today}}.
 */

export const TEMPLATE_VARIABLES = [
  // Driver
  { key: 'driver.first_name', label: 'First name', category: 'Driver', example: 'John' },
  { key: 'driver.last_name', label: 'Last name', category: 'Driver', example: 'Smith' },
  { key: 'driver.name', label: 'Full name', category: 'Driver', example: 'John Smith' },
  { key: 'driver.email', label: 'Email', category: 'Driver', example: 'john@example.com' },
  { key: 'driver.phone', label: 'Phone', category: 'Driver', example: '+1 555-000-0000' },
  // Agreement
  { key: 'agreement.start_date', label: 'Start date', category: 'Agreement', example: 'Jan 1, 2025' },
  { key: 'agreement.end_date', label: 'End date', category: 'Agreement', example: 'Jun 1, 2025' },
  { key: 'agreement.base_charge', label: 'Weekly base charge', category: 'Agreement', example: '$300' },
  { key: 'agreement.deposit', label: 'Deposit', category: 'Agreement', example: '$500' },
  // Car
  { key: 'car.year', label: 'Year', category: 'Car', example: '2022' },
  { key: 'car.make', label: 'Make', category: 'Car', example: 'Toyota' },
  { key: 'car.model', label: 'Model', category: 'Car', example: 'Camry' },
  { key: 'car.color', label: 'Color', category: 'Car', example: 'Black' },
  { key: 'car.vin', label: 'VIN', category: 'Car', example: '1HGBH41JXMN109186' },
  { key: 'car.plate', label: 'Plate', category: 'Car', example: 'ABC1234' },
  // System
  { key: 'today', label: "Today's date", category: 'System', example: 'March 30, 2025' },
  { key: 'now', label: 'Current date & time', category: 'System', example: 'March 30, 2025 2:00 PM' },
]

/**
 * Replaces {{variable}} placeholders in content using the provided context object.
 * Supports dot-notation: {{driver.first_name}} resolves to context.driver.first_name.
 * Unresolved variables are left as-is.
 */
export function resolveTemplateVariables(content, context) {
  if (!content || !context) return content
  return content.replace(/\{\{([^}]+)\}\}/g, (match, key) => {
    const parts = key.trim().split('.')
    let value = context
    for (const part of parts) {
      if (value == null) break
      value = value[part]
    }
    return value != null && value !== '' ? String(value) : match
  })
}

/**
 * Builds the template context object from a driver GraphQL node.
 * Always includes system variables (today, now).
 * Driver, agreement, and car keys are only set when data is available.
 */
export function buildTemplateContext(driverGqlData) {
  const now = new Date()
  const context = {
    today: now.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }),
    now: now.toLocaleString('en-US', { year: 'numeric', month: 'long', day: 'numeric', hour: 'numeric', minute: '2-digit' }),
  }

  if (!driverGqlData) return context

  context.driver = {
    first_name: driverGqlData.firstName || '',
    last_name: driverGqlData.lastName || '',
    name: driverGqlData.name || `${driverGqlData.firstName || ''} ${driverGqlData.lastName || ''}`.trim(),
    email: driverGqlData.email || '',
    phone: driverGqlData.phone || '',
  }

  const agreement = driverGqlData.currentAgreement
  if (agreement) {
    const fmtDate = (d) => d
      ? new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
      : ''
    const fmtMoney = (v) => (v != null && v !== '') ? `$${v}` : ''

    context.agreement = {
      start_date: fmtDate(agreement.startDate),
      end_date: fmtDate(agreement.endDate),
      base_charge: fmtMoney(agreement.baseCharge),
      deposit: fmtMoney(agreement.deposit),
    }

    const car = agreement.car
    if (car) {
      context.car = {
        year: car.year || '',
        make: car.carModel?.make || '',
        model: car.carModel?.name || '',
        color: car.color || '',
        vin: car.vin || '',
        plate: car.dmvPlate || '',
      }
    }
  }

  return context
}
